import React, { useEffect, useRef } from 'react';

interface HeatmapChartProps {
    data?: number[][];
    xLabels?: string[];
    yLabels?: string[];
    colorScale?: {
        min: string;
        max: string;
    };
    minValue?: number;
    maxValue?: number;
    cellPadding?: number;
    chartPadding?: { top: number; right: number; bottom: number; left: number } | number;
    fontSize?: number;
    lineHeight?: number;
    showValues?: boolean;
    showLabels?: boolean;
    borderColor?: string;
    height?: number;
    className?: string;
}

const HeatmapChart: React.FC<HeatmapChartProps> = ({
    data = [],
    xLabels = [],
    yLabels = [],
    colorScale = {
        min: 'rgba(147, 112, 219, 0.2)',
        max: 'rgba(147, 112, 219, 1)'
    },
    minValue = 0,
    maxValue = 100,
    cellPadding = 0.5,
    chartPadding = { top: 0, right: 10, bottom: 15, left: 30 },
    fontSize = 10,
    lineHeight,
    showValues = false,
    showLabels = true,
    borderColor = 'rgba(0, 0, 0, 0)',
    height = 270,
    className = ''
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    const getPadding = () => {
        if (typeof chartPadding === 'number') {
            return { top: chartPadding, right: chartPadding, bottom: chartPadding, left: chartPadding };
        }
        return chartPadding;
    };

    const getColor = (value: number) => {
        const intensity = (value - minValue) / (maxValue - minValue);
        const alpha = Math.max(0.2, Math.min(1, intensity));
        return `rgba(147, 112, 219, ${alpha})`;
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const padding = getPadding();
        const effectiveLineHeight = lineHeight || fontSize;

        const draw = () => {
            requestAnimationFrame(() => {
                const rect = canvas.getBoundingClientRect();
                canvas.width = rect.width;
                canvas.height = rect.height;

                const width = canvas.width;
                const height = canvas.height;

                const safeData = data || [];
                const safeXLabels = xLabels || [];
                const safeYLabels = yLabels || [];

                if (safeXLabels.length === 0 || safeYLabels.length === 0) return;

                const chartWidth = width - padding.left - padding.right;
                const chartHeight = height - padding.top - padding.bottom;

                const cellWidth = chartWidth / safeXLabels.length;
                const cellHeight = chartHeight / safeYLabels.length;

                ctx.clearRect(0, 0, width, height);

                safeData.forEach((row, rowIndex) => {
                    row.forEach((value, colIndex) => {
                        const x = padding.left + colIndex * cellWidth;
                        const y = padding.top + rowIndex * cellHeight;

                        ctx.fillStyle = getColor(value);
                        ctx.fillRect(
                            x + cellPadding,
                            y + cellPadding,
                            cellWidth - cellPadding * 2,
                            cellHeight - cellPadding * 2
                        );

                        ctx.strokeStyle = borderColor;
                        ctx.lineWidth = 0;
                        ctx.strokeRect(
                            x + cellPadding,
                            y + cellPadding,
                            cellWidth - cellPadding * 2,
                            cellHeight - cellPadding * 2
                        );

                        if (showValues) {
                            ctx.fillStyle = value > (maxValue * 0.6) ? '#ffffff' : '#333333';
                            ctx.font = `${fontSize}px/${effectiveLineHeight}px Arial`;
                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'middle';
                            ctx.fillText(
                                value.toString(),
                                x + cellWidth / 2,
                                y + cellHeight / 2
                            );
                        }
                    });
                });

                if (showLabels) {
                    ctx.fillStyle = '#888888';
                    ctx.font = `${fontSize}px Arial`;

                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'top';
                    safeXLabels.forEach((label, index) => {
                        const x = padding.left + index * cellWidth + cellWidth / 2;
                        ctx.fillText(label, x, height - padding.bottom + 5);
                    });

                    ctx.textAlign = 'right';
                    ctx.textBaseline = 'middle';
                    safeYLabels.forEach((label, index) => {
                        const y = padding.top + index * cellHeight + cellHeight / 2;
                        ctx.fillText(label, padding.left - 10, y);
                    });
                }
            });
        };

        draw();

        // Debounced resize handler to prevent excessive redraws
        let resizeTimeout: number;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = window.setTimeout(() => {
                draw();
            }, 150); // 150ms debounce
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, [data, xLabels, yLabels, colorScale, minValue, maxValue, cellPadding, chartPadding, fontSize, lineHeight, showValues, showLabels, borderColor]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const padding = getPadding();
        const safeData = data || [];
        const safeXLabels = xLabels || [];
        const safeYLabels = yLabels || [];

        let tooltip = document.getElementById('heatmap-tooltip');
        if (!tooltip) {
            tooltip = document.createElement('div');
            tooltip.id = 'heatmap-tooltip';
            tooltip.style.cssText = `
                position: fixed;
                background: rgba(0, 0, 0, 0.8);
                color: white;
                padding: 8px 12px;
                border-radius: 4px;
                font-size: 12px;
                pointer-events: none;
                display: none;
                z-index: 1000;
                white-space: nowrap;
            `;
            document.body.appendChild(tooltip);
        }
        tooltipRef.current = tooltip as HTMLDivElement;

        const handleMouseMove = (e: MouseEvent) => {
            if (!tooltipRef.current || !canvas) return;

            const rect = canvas.getBoundingClientRect();
            const chartWidth = canvas.width - padding.left - padding.right;
            const chartHeight = canvas.height - padding.top - padding.bottom;
            const cellWidth = chartWidth / safeXLabels.length;
            const cellHeight = chartHeight / safeYLabels.length;

            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            if (x >= padding.left && x <= canvas.width - padding.right &&
                y >= padding.top && y <= canvas.height - padding.bottom) {

                const colIndex = Math.floor((x - padding.left) / cellWidth);
                const rowIndex = Math.floor((y - padding.top) / cellHeight);

                if (rowIndex >= 0 && rowIndex < safeYLabels.length &&
                    colIndex >= 0 && colIndex < safeXLabels.length) {

                    const value = safeData[rowIndex]?.[colIndex];
                    if (value !== undefined) {
                        tooltipRef.current.textContent = `${safeYLabels[rowIndex]} ${safeXLabels[colIndex]}: ${value} visitors`;
                        tooltipRef.current.style.display = 'block';

                        tooltipRef.current.style.position = 'fixed';
                        tooltipRef.current.style.left = (e.clientX + 10) + 'px';
                        tooltipRef.current.style.top = (e.clientY + 10) + 'px';

                        canvas.style.cursor = 'pointer';
                        return;
                    }
                }
            }

            tooltipRef.current.style.display = 'none';
            canvas.style.cursor = 'default';
        };

        const handleMouseLeave = () => {
            if (tooltipRef.current) {
                tooltipRef.current.style.display = 'none';
            }
            if (canvas) {
                canvas.style.cursor = 'default';
            }
        };

        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseleave', handleMouseLeave);

            if (tooltipRef.current) {
                tooltipRef.current.style.display = 'none';
            }
        };
    }, [data, xLabels, yLabels, chartPadding]);

    return (
        <canvas
            ref={canvasRef}
            className={`d-block ${className}`}
            style={{ width: '100%', height: height }}
        />
    );
};

export default HeatmapChart;
